import { useState, useEffect, useCallback } from 'react';
import {
  CreditCardOutlined,
  EditOutlined,
  PercentageOutlined,
  PlusCircleOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import { useToast } from '../../hooks/toast';
import PaymentMethodCreateEdit from './CreateEdit';
import { IPaymentMethod } from '../../shared/interfaces';
import {
  ATable,
  MenuOptions,
  StatusActive,
  DropdownOptions,
  PageIntro,
  PageLayout,
  PagePanel,
} from '../../components';
import { useNavigate } from 'react-router-dom';
import { getPaymentsMethods } from '../../http';
import { useAuth } from '../../hooks/auth';
import { PermissionActions } from '../../shared/enums/permissions.enum';
import { Screens } from '../../shared/enums/screens.enum';

const rowSelection = {
  onChange: (selectedRowKeys: React.Key[], selectedRows: IPaymentMethod[]) => {
    console.log(
      `selectedRowKeys: ${selectedRowKeys}`,
      'selectedRows: ',
      selectedRows,
    );
  },
};

const PaymentsMethods: React.FC = () => {
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { permissionFind, screenFind } = useAuth();
  const [search, setSearch] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState<IPaymentMethod | null>(
    null,
  );
  const [modalCreate, setModalCreate] = useState<boolean>(false);
  const [paymentsMethods, setPaymentsMethods] = useState<IPaymentMethod[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>();

  const list = useCallback(async () => {
    setLoading(true);
    setPaymentsMethods([]);
    try {
      const response = await getPaymentsMethods(
        limit,
        (page - 1) * limit,
        search,
      );
      setPages(response.count);
      setPaymentsMethods(response.data);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Erro ao listar planos de contas',
      });
    } finally {
      setLoading(false);
    }
  }, [limit, search, page, addToast]);

  const renderModalPaymentMethodCreateEdit = () => {
    if (modalCreate)
      return (
        <PaymentMethodCreateEdit
          open={modalCreate}
          close={() => setModalCreate(false)}
          paymentMethod={paymentMethod}
          setPaymentsMethods={setPaymentsMethods}
        />
      );
  };
  useEffect(() => {
    list();
  }, [page, search, limit, list]);

  return (
    <PageLayout>
      <PageIntro
        badge="Financeiro"
        icon={<CreditCardOutlined />}
        title="Formas de pagamento"
        description="Organize os meios de pagamento aceitos pela clínica e acesse as taxas vinculadas com mais facilidade."
      />
      <MenuOptions
        menu_items={[
          {
            label: 'Atualizar',
            key: '1',
            icon: <RedoOutlined />,
            onClick: () => list(),
          },
          permissionFind([PermissionActions.paymentsMethodsCreate])
            ? {
                label: 'Cadastrar',
                key: '2',
                icon: <PlusCircleOutlined />,
                onClick: () => {
                  setPaymentMethod(null);
                  setModalCreate(true);
                },
              }
            : null,
        ]}
        showInputSearch
        search={search}
        setSearch={setSearch}
      />
      <PagePanel>
        <ATable
          rowSelection={{
            type: 'checkbox',
            ...rowSelection,
          }}
          scroll={{ y: 510 }}
          columns={[
            {
              title: 'Id',
              width: 100,
              render: (value, record, index) => <>{index + 1}</>,
            },
            {
              title: 'Descrição',
              dataIndex: 'description',
            },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 95,
              render: (value: boolean) => <StatusActive status={value} />,
            },
            {
              title: 'Opções',
              width: 130,
              render: (cell: IPaymentMethod) => (
                <DropdownOptions
                  items={[
                    permissionFind([PermissionActions.paymentsMethodsUpdate])
                      ? {
                          label: 'Editar',
                          key: '1',
                          icon: <EditOutlined />,
                          onClick: () => {
                            setPaymentMethod(cell);
                            setModalCreate(true);
                          },
                        }
                      : null,
                    screenFind([Screens.taxesView])
                      ? {
                          label: 'Taxas',
                          key: '2',
                          icon: <PercentageOutlined />,
                          onClick: () => navigate(`/taxes/${cell.id}`),
                        }
                      : null,
                  ]}
                />
              ),
            },
          ]}
          loading={loading}
          dataSource={paymentsMethods}
          page={page}
          setPage={setPage}
          pages={pages}
          limit={limit}
          setLimit={setLimit}
        />
      </PagePanel>
      {renderModalPaymentMethodCreateEdit()}
    </PageLayout>
  );
};

export default PaymentsMethods;
